import {
  Stripe,
  StripeCardNumberElement,
  StripeElements,
} from "@stripe/stripe-js";
import { analytics } from "_shared/utils/analytics";
import { API, Auth } from "aws-amplify";
import { FormikErrors, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { Route, Switch, useHistory, useLocation } from "react-router-dom";
import Loading from "../../_shared/components/Loading/Loading";
import { useModal } from "../../_shared/contexts/ModalContext";
import { FormValues } from "./_shared/models/FormValues";
import { ProductDetails } from "./_shared/models/ProductDetails";
import PayWithCard from "./payWithCard/PayWithCard";
import Register from "./register/Register";
import SelectAddOns from "./selectAddOns/SelectAddOns";
import SelectPlan from "./selectPlan/SelectPlan";
import { usePostHog } from "posthog-js/react";

const Routes = () => {
  const posthog = usePostHog();
  const history = useHistory();
  const { pathname, state } = useLocation();
  const { onErrorMsg } = useModal();

  const [step, setStep] = useState(1);
  const [loadingMsg, setLoadingMsg] = useState<string | null>(null);
  const [userSub, setUserSub] = useState<string>();
  const [productDetails, setProductDetails] = useState<
    Record<string, ProductDetails>
  >({});
  const [steps, setSteps] = useState([
    "/signup",
    "/signup/select-plan",
    "/signup/select-add-ons",
    "/signup/pay-with-card",
  ]);
  const { values, setFieldValue } = useFormikContext<FormValues>();

  useEffect(() => {
    // @ts-ignore
    if (state?.verified42Macro) {
      analytics.track("Signup Started");
      setFieldValue("plan", "plus_plan");
      // @ts-ignore
      setFieldValue("verified42Macro", state?.verified42Macro);
      setSteps(["/signup", "/signup/select-add-ons", "/signup/pay-with-card"]);
    } else {
      analytics.track("Signup Started");
    }
  }, [state]);

  useEffect(() => {
    // get subscriptions details from cognito database
    const fetchPlans = async () => {
      const subscriptions = await API.get(
        "public",
        "/cognito/subscriptions",
        {},
      );
      setProductDetails(subscriptions);
    };
    fetchPlans();
  }, []);

  useEffect(() => {
    // set current step when routing changes
    setStep(steps.indexOf(pathname));
    // scroll to top on every route change
    window.scrollTo(0, 0);
  }, [pathname]);

  const handleStepBack = () => {
    if (step === 0) return;
    history.push(steps[step - 1]);
  };

  const handleStepForward = () => {
    if (step >= steps.length - 1) return;
    history.push(steps[step + 1]);
  };

  async function handleSignup(
    values: FormValues,
    errors: FormikErrors<FormValues>,
  ) {
    const errorMessage =
      errors.firstName ||
      errors.lastName ||
      errors.email ||
      errors.phone ||
      errors.password ||
      errors.confirmPassword;

    if (errorMessage) {
      onErrorMsg(errorMessage);
      return;
    }
    setLoadingMsg("Signing up...");

    const customAttributes = {
      "custom:firstName": values.firstName,
      "custom:lastName": values.lastName,
      "custom:phoneNumber": values.phone,
      "custom:areaCode": values.areaCode,
      "custom:brokerage": values.brokerage,
      "custom:referral": values.referral,
      "custom:twitter": values.twitter,
    };

    try {
      const user = await Auth.signUp({
        username: values.email,
        password: values.password,
        attributes: customAttributes,
      });
      posthog?.identify(user?.userSub, { email: values.email });
      setUserSub(user.userSub);
      // create dashboard behind the scenes.
      API.post("public", "/cognito/createDashboard", {
        body: {
          sub: user.userSub,
          attributes: {
            email: values.email,
            ...customAttributes,
          },
          verified42Macro: values.verified42Macro,
        },
      });
      handleStepForward();
    } catch (err: any) {
      let errorMessage =
        "There was an issue registering your account. Please try again later.";
      // if an account with the current email already exists
      if (err.code === "UsernameExistsException") {
        try {
          // delete user and try again if possible (if account is not paid)
          const deleteUserResponse = await API.post(
            "public",
            "/cognito/deleteUser",
            {
              body: { email: values.email },
            },
          );
          if (deleteUserResponse === "success") {
            // try signup again after deletion
            handleSignup(values, errors);
            return;
          }
        } catch {
          errorMessage =
            "A paid account with this email already exists. Please use a different email address.";
        }
      }
      onErrorMsg(errorMessage);
    }
    setLoadingMsg(null);
  }

  async function handleConfirmAccount(
    values: FormValues,
    errors: FormikErrors<FormValues>,
    stripe: Stripe | null,
    elements: StripeElements | null,
  ) {
    // disable form submission until Stripe.js has loaded.
    if (!stripe || !elements) return;

    const errorMessage =
      errors.billingName ||
      errors.billingCountry ||
      errors.billingZipCode ||
      errors.acceptsTerms;

    if (errorMessage) {
      setLoadingMsg(null);
      onErrorMsg(errorMessage);
      return;
    }

    const selectedPlan = productDetails?.[
      values.plan as keyof ProductDetails
    ] as ProductDetails;
    const selectedAddons = Object.values(productDetails!).filter((product) =>
      values.addOns.includes(product.name),
    );

    if (
      (selectedPlan.key === "free_plan" ||
        (selectedPlan.key === "plus_plan" && values.verified42Macro)) &&
      !selectedAddons?.length
    ) {
      setLoadingMsg("Creating dashboard...");
    } else {
      setLoadingMsg("Processing Payment...");
    }

    // create customer
    const newCustomer = await API.post("public", "/cognito/createCustomer", {
      body: {
        name: values.billingName,
        email: values.email,
        sub: userSub,
      },
    });

    // pass customer details to stripe
    if (
      !(
        (selectedPlan.key === "free_plan" ||
          (selectedPlan.key === "plus_plan" && values.verified42Macro)) &&
        !selectedAddons?.length
      )
    ) {
      const result = await stripe.confirmCardSetup(newCustomer.client_secret, {
        payment_method: {
          card: elements.getElement("cardNumber") as StripeCardNumberElement,
          billing_details: {
            name: values.billingName,
            email: values.email,
          },
        },
      });
      if (result.error) {
        onErrorMsg(result.error.message as string);
        setLoadingMsg(null);
        return;
      }
    }

    if (!selectedPlan || !selectedAddons) return;
    //POST customer details to database.
    const confirmCustomer = await API.post(
      "public",
      "/cognito/confirmCustomer",
      {
        body: {
          email: values.email,
          sub: userSub,
          customer_id: newCustomer.customer_id,
          plan_price_id: selectedPlan[`monthly_${values.frequency}_price_id`],
          addon_price_ids: selectedAddons.map(
            (addOn) => addOn[`monthly_${values.frequency}_price_id`],
          ),
          // TODO: promo needs to be checked before it gets passed here
          promo: values.verified42Macro ? "42MACROBUNDLEACCESS" : values.promo,
        },
      },
    );
    //If successful, redirect.
    if (confirmCustomer.message === "success") {
      setLoadingMsg(null);
      history.push({
        pathname: "/thank-you",
        state: {
          email: values.email,
          // password allows user to 'continue to dashboard'
          password: values.password,
        },
      });
      analytics.track("Signup Completed", {
        plan: selectedPlan.name,
        addons: selectedAddons.map((addOn) => addOn.name),
        frequency: values.frequency,
        promo: values.promo,
      });
    } else {
      setLoadingMsg(null);
      onErrorMsg(confirmCustomer.detail); // TODO: is this object value valid?
    }
  }

  return (
    <>
      <div id="sign-up-page">
        <div className="card">
          <div className="progress-bar">
            <div
              className="progress-bar-inner"
              style={{ width: 25 * (step + 1) + "%" }}
            />
          </div>
          <Switch>
            <Route exact path="/signup">
              <Register onNext={handleSignup} />
            </Route>
            <Route path="/signup/select-plan">
              <SelectPlan
                productDetails={productDetails}
                onBack={handleStepBack}
                onNext={handleStepForward}
              />
            </Route>
            <Route path="/signup/select-add-ons">
              <SelectAddOns
                productDetails={productDetails}
                onBack={handleStepBack}
                onNext={handleStepForward}
              />
            </Route>
            <Route path="/signup/pay-with-card">
              <PayWithCard
                productDetails={productDetails}
                onBack={handleStepBack}
                onNext={handleConfirmAccount}
              />
            </Route>
          </Switch>
        </div>
      </div>
      {loadingMsg && <Loading message={loadingMsg} />}
    </>
  );
};

export default Routes;
